import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  Grid,
  Link as MuiLink
} from '@mui/material';
import {
  Business,
  Phone,
  Email,
  LocationOn,
  Language,
  Person
} from '@mui/icons-material';
import { MEMBER_TERM } from '../config/clubTerms';

/**
 * 公開 EDM 頁面
 * 不需要登入即可存取
 * 透過 share_token 顯示用戶的公開 EDM 資訊
 */
function PublicEDM() {
  const { shareToken } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPublicProfile = async () => {
      try {
        const response = await axios.get(`/api/public/edm/${shareToken}`);
        
        if (response.data.success) {
          setProfile(response.data.profile);
        } else {
          setError('無法載入資料');
        }
      } catch (err) {
        console.error('載入公開資料失敗:', err);
        setError(err.response?.data?.error || '載入失敗，請稍後再試');
      } finally {
        setLoading(false);
      }
    };

    if (shareToken) {
      fetchPublicProfile();
    } else {
      setError('無效的分享連結');
      setLoading(false);
    }
  }, [shareToken]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          載入中...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Container>
    );
  }

  if (!profile) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="warning">
          找不到資料
        </Alert>
      </Container>
    );
  }

  const edmImageUrl = profile.edmPhoto
    ? `/uploads/EDM/${profile.edmPhoto}`
    : null;

  return (
    <>
      {/* 動態設置 Open Graph meta 標籤 */}
      <Helmet>
        <title>{profile.og?.title || `${profile.name} - ${process.env.REACT_APP_CLUB_SHORT_NAME || 'TWVN'}`}</title>
        <meta property="og:title" content={profile.og?.title || `${profile.name} - ${process.env.REACT_APP_CLUB_SHORT_NAME || 'TWVN'}`} />
        <meta property="og:description" content={profile.og?.description || `${profile.name} 的公司簡介`} />
        <meta property="og:image" content={profile.og?.image || window.location.origin + '/logo192.png'} />
        <meta property="og:url" content={profile.og?.url || window.location.href} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={process.env.REACT_APP_CLUB_FULL_NAME || 'TWVN'} />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={profile.og?.title || `${profile.name} - ${process.env.REACT_APP_CLUB_SHORT_NAME || 'TWVN'}`} />
        <meta name="twitter:description" content={profile.og?.description || `${profile.name} 的公司簡介`} />
        <meta name="twitter:image" content={profile.og?.image || window.location.origin + '/logo192.png'} />

        {/* 基本 meta */}
        <meta name="description" content={profile.og?.description || `${profile.name} 的公司簡介`} />
      </Helmet>

      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        {/* 頁面標題 */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            公司簡介
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {`${process.env.REACT_APP_CLUB_SHORT_NAME || 'TWVN'} ${MEMBER_TERM}資訊分享`}
          </Typography>
        </Box>

      {/* 主卡片 */}
      <Card elevation={3}>
        <CardContent>
          {/* 個人資訊區塊 */}
          <Box sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                <Person />
              </Avatar>
              <Box>
                <Typography variant="h5" component="div">
                  {profile.name}
                </Typography>
                {profile.storeName && (
                  <Typography variant="body2" color="textSecondary">
                    {profile.storeName}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* 公司資訊區塊 */}
          <Grid container spacing={2}>
            {profile.companyName && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="center">
                  <Business sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      公司名稱
                    </Typography>
                    <Typography variant="body1">
                      {profile.companyName}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {profile.position && (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <Person sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      職位
                    </Typography>
                    <Typography variant="body1">
                      {profile.position}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {profile.mobile && (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <Phone sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      手機
                    </Typography>
                    <MuiLink href={`tel:${profile.mobile}`} underline="hover">
                      {profile.mobile}
                    </MuiLink>
                  </Box>
                </Box>
              </Grid>
            )}

            {profile.phone && (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <Phone sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      電話
                    </Typography>
                    <MuiLink href={`tel:${profile.phone}`} underline="hover">
                      {profile.phone}
                    </MuiLink>
                  </Box>
                </Box>
              </Grid>
            )}

            {profile.email && (
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center">
                  <Email sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Email
                    </Typography>
                    <MuiLink href={`mailto:${profile.email}`} underline="hover">
                      {profile.email}
                    </MuiLink>
                  </Box>
                </Box>
              </Grid>
            )}

            {profile.address && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="start">
                  <LocationOn sx={{ mr: 1, color: 'primary.main', mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      公司地址
                    </Typography>
                    <Typography variant="body1">
                      {profile.address}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}

            {profile.website && (
              <Grid item xs={12}>
                <Box display="flex" alignItems="center">
                  <Language sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      公司網站
                    </Typography>
                    <MuiLink 
                      href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      {profile.website}
                    </MuiLink>
                  </Box>
                </Box>
              </Grid>
            )}

            {profile.businessScope && (
              <Grid item xs={12}>
                <Box>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    營業項目
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {profile.businessScope}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* EDM 圖片區塊 */}
          {edmImageUrl && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  公司簡介 EDM
                </Typography>
                <Box
                  component="img"
                  src={edmImageUrl}
                  alt="公司簡介 EDM"
                  sx={{
                    width: '100%',
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: 2,
                    boxShadow: 3,
                    cursor: 'pointer'
                  }}
                  onClick={() => window.open(edmImageUrl, '_blank')}
                />
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  點擊圖片可放大查看
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

        {/* 頁尾資訊 */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="body2" color="textSecondary">
            由 {(process.env.REACT_APP_CLUB_SHORT_NAME || 'TWVN') + ' 活動報名系統'} 提供
          </Typography>
        </Box>
      </Container>
    </>
  );
}

export default PublicEDM;
